import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminStats from '../components/admin/AdminStats';
import UserManagement from '../components/admin/UserManagement';
import DomainManagement from '../components/admin/DomainManagement';
import EmailManagement from '../components/admin/EmailManagement';
import OrderManagement from '../components/admin/OrderManagement';
import HostingManagement from '../components/admin/HostingManagement';
import HostingAccountsManagement from '../components/admin/HostingAccountsManagement';
import MailDomainsManagement from '../components/admin/MailDomainsManagement';
import VPSManagement from '../components/admin/VPSManagement';
import SupportManagement from '../components/admin/SupportManagement';
import S3BackupManagement from '../components/admin/S3BackupManagement';
import HostDensityDashboard from '../components/admin/HostDensityDashboard';
import BusinessEmailManagement from '../components/admin/BusinessEmailManagement';
import AdminNav from '../components/admin/AdminNav';
import logo from '../assets/logo.png';

type AdminTab = 'dashboard' | 'users' | 'domains' | 'emails' | 'business-email' | 'hosting' | 'hosting-accounts' | 'mail-domains' | 'vps' | 'orders' | 'support' | 'backups' | 'host-density';

const Admin: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [stats, setStats] = useState<any>(null);

  // Set active tab based on URL path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin/users') {
      setActiveTab('users');
    } else if (path === '/admin/orders') {
      setActiveTab('orders');
    } else if (path === '/admin/domains') {
      setActiveTab('domains');
    } else if (path === '/admin/emails') {
      setActiveTab('emails');
    } else if (path === '/admin/business-email') {
      setActiveTab('business-email');
    } else if (path === '/admin/hosting') {
      setActiveTab('hosting');
    } else if (path === '/admin/hosting-accounts') {
      setActiveTab('hosting-accounts');
    } else if (path === '/admin/mail-domains') {
      setActiveTab('mail-domains');
    } else if (path === '/admin/vps') {
      setActiveTab('vps');
    } else if (path === '/admin/support') {
      setActiveTab('support');
    } else if (path === '/admin/backups') {
      setActiveTab('backups');
    } else if (path === '/admin/host-density') {
      setActiveTab('host-density');
    } else {
      setActiveTab('dashboard');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminStats();
    }
    
    // Set default tab based on user role
    if (user?.role === 'support' && location.pathname === '/admin') {
      navigate('/admin/support');
    }
  }, [user, location.pathname, navigate]);

  const fetchAdminStats = async () => {
    // This will be implemented with admin-specific endpoints
    console.log('Fetching admin stats...');
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/admin';
  };


  // Admin nav is grouped into a few top-level menus so the bar fits on one
  // row and related pages live together. The flat 12-item list outgrew the
  // header. Single items (Dashboard) render as their own button; grouped
  // items render as a dropdown. Role gating happens per-item — a group is
  // hidden entirely if no item in it is visible to the current role.
  type TabItem = { id: AdminTab; name: string; icon: string; roles: string[] };
  type NavEntry =
    | { kind: 'item'; item: TabItem }
    | { kind: 'group'; label: string; icon: string; items: TabItem[] };

  const allItems: TabItem[] = [
    { id: 'dashboard',         name: 'Dashboard',        icon: '📊', roles: ['admin', 'support'] },
    { id: 'support',           name: 'Support Tickets',  icon: '🎫', roles: ['admin', 'support'] },
    { id: 'users',             name: 'Users',            icon: '👥', roles: ['admin'] },
    { id: 'orders',            name: 'Orders',           icon: '🛒', roles: ['admin'] },
    { id: 'hosting',           name: 'Hosting Packages', icon: '🖥️', roles: ['admin'] },
    { id: 'hosting-accounts',  name: 'Hosting Accounts', icon: '🏠', roles: ['admin', 'support'] },
    { id: 'mail-domains',      name: 'Mail Domains',     icon: '✉️', roles: ['admin', 'support'] },
    { id: 'vps',               name: 'VPS Packages',     icon: '🚀', roles: ['admin'] },
    { id: 'emails',            name: 'Email Packages',   icon: '📧', roles: ['admin'] },
    { id: 'business-email',    name: 'Business Email',   icon: '✉️', roles: ['admin', 'support'] },
    { id: 'domains',           name: 'Domains',          icon: '🌐', roles: ['admin'] },
    { id: 'backups',           name: 'S3 Backups',       icon: '💾', roles: ['admin'] },
    { id: 'host-density',      name: 'Host Density',     icon: '📈', roles: ['admin'] },
  ];
  const byId = Object.fromEntries(allItems.map(i => [i.id, i])) as Record<AdminTab, TabItem>;

  const role = user?.role || '';
  const filterRole = (i: TabItem) => i.roles.includes(role);
  const filterGroup = (items: TabItem[]) => items.filter(filterRole);

  const navEntries: NavEntry[] = [
    { kind: 'item', item: byId['dashboard'] },
    {
      kind: 'group', label: 'Customers', icon: '👥',
      items: filterGroup([byId['users'], byId['orders'], byId['support']]),
    },
    {
      kind: 'group', label: 'Services', icon: '🛍️',
      items: filterGroup([
        byId['hosting'], byId['hosting-accounts'],
        byId['vps'],
        byId['emails'], byId['business-email'], byId['mail-domains'],
        byId['domains'],
      ]),
    },
    {
      kind: 'group', label: 'Infrastructure', icon: '⚙️',
      items: filterGroup([byId['host-density'], byId['backups']]),
    },
  ].filter(e =>
    e.kind === 'item' ? filterRole(e.item) : e.items.length > 0,
  ) as NavEntry[];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-gradient-to-r from-brand-navy to-gray-800 shadow-lg">
        <div className="px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="bg-white rounded-lg p-2 shadow-sm">
                <img src={logo} alt="12and22" className="h-8 w-auto" />
              </div>
              <div className="border-l border-gray-600 pl-4">
                <h1 className="text-xl font-bold text-white">
                  {user?.role === 'admin' ? 'Administration Panel' : 'Support Dashboard'}
                </h1>
                <p className="text-xs text-gray-300">
                  {user?.role === 'admin' ? '12and22 Hosting Management' : '12and22 Support Center'}
                </p>
              </div>
            </div>

            {/* Admin Info and Actions */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-gray-300">{user?.email}</p>
              </div>
              <div className="flex items-center space-x-2">
                {/* Role badge: admin uses brand yellow (logo accent), support
                    uses brand navy. Both stay in the logo family. */}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  user?.role === 'admin'
                    ? 'bg-secondary-500 text-primary-navy'
                    : 'bg-primary-navy text-secondary-300'
                }`}>
                  {user?.role === 'admin' ? 'Administrator' : 'Support Staff'}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-primary-navy hover:bg-primary-700 border border-secondary-500/40 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 lg:px-8">
          <AdminNav
            entries={navEntries}
            activeTab={activeTab}
            onNavigate={(id) => navigate(id === 'dashboard' ? '/admin' : `/admin/${id}`)}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'dashboard' && <AdminStats />}
          {activeTab === 'support' && <SupportManagement />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'orders' && <OrderManagement />}
          {activeTab === 'domains' && <DomainManagement />}
          {activeTab === 'emails' && <EmailManagement />}
          {activeTab === 'business-email' && <BusinessEmailManagement />}
          {activeTab === 'hosting' && <HostingManagement />}
          {activeTab === 'hosting-accounts' && <HostingAccountsManagement />}
          {activeTab === 'mail-domains' && <MailDomainsManagement />}
          {activeTab === 'vps' && <VPSManagement />}
          {activeTab === 'host-density' && <HostDensityDashboard />}
          {activeTab === 'backups' && <S3BackupManagement />}
        </div>
      </div>
    </div>
  );
};

export default Admin;